import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileTextIcon, MailIcon, ArrowRightIcon } from '../../components/icons/Icons';
import api from '../../services/api';
import AdminLayout from './AdminLayout';

const Dashboard = () => {
  const [stats, setStats] = useState({
    posts: 0,
    publishedPosts: 0,
    messages: 0,
    unreadMessages: 0
  });
  const [recentPosts, setRecentPosts] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [postsRes, messagesRes] = await Promise.all([
        api.get('/blog/admin/all'),
        api.get('/contact')
      ]);

      const posts = postsRes.data;
      const messages = messagesRes.data;

      setStats({
        posts: posts.length,
        publishedPosts: posts.filter(p => p.status === 'published').length,
        messages: messages.length,
        unreadMessages: messages.filter(m => !m.is_read).length
      });

      setRecentPosts(posts.slice(0, 5));
      setRecentMessages(messages.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Dashboard</h1>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-stats">
        <div className="stat-card-admin">
          <div className="stat-icon">
            <FileTextIcon size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.posts}</span>
            <span className="stat-label">Total Posts</span>
          </div>
        </div>
        <div className="stat-card-admin">
          <div className="stat-icon published">
            <FileTextIcon size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.publishedPosts}</span>
            <span className="stat-label">Published</span>
          </div>
        </div>
        <div className="stat-card-admin">
          <div className="stat-icon messages">
            <MailIcon size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.messages}</span>
            <span className="stat-label">Messages</span>
          </div>
        </div>
        <div className="stat-card-admin">
          <div className="stat-icon unread">
            <MailIcon size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.unreadMessages}</span>
            <span className="stat-label">Unread</span>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Recent Posts */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Recent Posts</h3>
            <Link to="/admin/posts" className="admin-link">
              View All <ArrowRightIcon size={16} />
            </Link>
          </div>
          {loading ? (
            <p>Loading...</p>
          ) : recentPosts.length === 0 ? (
            <p className="admin-empty">No posts yet</p>
          ) : (
            <div className="dashboard-list">
              {recentPosts.map(post => (
                <div key={post.id} className="dashboard-list-item">
                  <div className="dashboard-list-info">
                    <h4>{post.title}</h4>
                    <span>{formatDate(post.created_at)}</span>
                  </div>
                  <span className={`status-badge status-${post.status}`}>
                    {post.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Messages */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Recent Messages</h3>
            <Link to="/admin/messages" className="admin-link">
              View All <ArrowRightIcon size={16} />
            </Link>
          </div>
          {loading ? (
            <p>Loading...</p>
          ) : recentMessages.length === 0 ? (
            <p className="admin-empty">No messages yet</p>
          ) : (
            <div className="dashboard-list">
              {recentMessages.map(msg => (
                <div key={msg.id} className="dashboard-list-item">
                  <div className="dashboard-list-info">
                    <h4>{msg.name}</h4>
                    <span>{msg.subject || 'No subject'}</span>
                  </div>
                  <span className={`status-badge status-${msg.is_read ? 'read' : 'unread'}`}>
                    {msg.is_read ? 'Read' : 'New'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .dashboard-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 32px;
        }

        .stat-card-admin {
          background: white;
          padding: 24px;
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow);
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .stat-icon {
          width: 56px;
          height: 56px;
          background: var(--primary);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .stat-icon.published { background: var(--accent); }
        .stat-icon.messages { background: var(--secondary); }
        .stat-icon.unread { background: #3b82f6; }

        .stat-value {
          display: block;
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--text-primary);
        }

        .stat-info .stat-label {
          font-size: 0.85rem;
          color: var(--text-secondary);
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }

        .admin-link {
          display: flex;
          align-items: center;
          gap: 6px;
          color: var(--secondary);
          font-size: 0.9rem;
          font-weight: 500;
        }

        .admin-link:hover {
          gap: 10px;
        }

        .admin-empty {
          color: var(--text-light);
          text-align: center;
          padding: 40px;
        }

        .dashboard-list-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 0;
          border-bottom: 1px solid var(--border);
        }

        .dashboard-list-item:last-child {
          border-bottom: none;
        }

        .dashboard-list-info h4 {
          font-family: 'Inter', sans-serif;
          font-size: 0.95rem;
          font-weight: 500;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .dashboard-list-info span {
          font-size: 0.8rem;
          color: var(--text-light);
        }

        @media (max-width: 1024px) {
          .dashboard-stats {
            grid-template-columns: repeat(2, 1fr);
          }
          .dashboard-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 600px) {
          .dashboard-stats {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </AdminLayout>
  );
};

export default Dashboard;
